import { AuthStorage } from '../types/auth.types';
import type { StorageChangeCallback, StorageChangeField, TokenStorageStrategy } from './TokenStorageStrategy';

const STORAGE_KEY_TO_FIELD: Record<string, StorageChangeField> = {
  [AuthStorage.weave_token]: 'token',
  [AuthStorage.jti]: 'jti',
};

/**
 * Default TokenStorageStrategy implementation for browser environments, using localStorage.
 *
 * Only responsible for getting/setting/removing and notifying of changes via the onStorageChange callback.
 * Caching and other token management logic is handled by the TokenStorage class that wraps this.
 */
export class BrowserTokenStorageStrategy implements TokenStorageStrategy {
  public name = 'BrowserTokenStorageStrategy';

  init() {
    // nothing to do in this strategy but we have to implement the method to satisfy the interface
    return this;
  }

  getToken(): string | undefined {
    return localStorage.getItem(AuthStorage.weave_token) ?? undefined;
  }

  setToken(token: string): void {
    localStorage.setItem(AuthStorage.weave_token, token);
  }

  removeToken(): void {
    localStorage.removeItem(AuthStorage.weave_token);
  }

  getJTI(): string | undefined {
    return localStorage.getItem(AuthStorage.jti) ?? undefined;
  }

  setJTI(jti: string): void {
    localStorage.setItem(AuthStorage.jti, jti);
  }

  removeJTI(): void {
    localStorage.removeItem(AuthStorage.jti);
  }

  getTokenReceivedAt(): number | undefined {
    const data = localStorage.getItem(AuthStorage.token_received_at);
    if (!data) return undefined;
    const parsed = parseInt(data, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  setTokenReceivedAt(value: number): void {
    localStorage.setItem(AuthStorage.token_received_at, value.toString());
  }

  removeTokenReceivedAt(): void {
    localStorage.removeItem(AuthStorage.token_received_at);
  }

  clearStoredAuthData(): void {
    localStorage.removeItem(AuthStorage.weave_token);
    localStorage.removeItem(AuthStorage.jti);
    localStorage.removeItem(AuthStorage.token_received_at);
  }

  // Provides TokenStorage with the way to listen to changes but doesn't do any logic of it's own -- that's up to TokenStorage.
  onStorageChange(cb: StorageChangeCallback): () => void {
    const handler = (e: StorageEvent) => {
      // Only respond to changes for our relevant keys, and map them to the appropriate field name for the callback.
      const field = e.key ? STORAGE_KEY_TO_FIELD[e.key] : undefined;
      if (field) {
        cb(field, e.newValue);
      }
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }
}
